import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Avatar from "@/components/Avatar";
import { ClipboardCheck, Send, Save, Coffee, Eye, ArrowLeft, Plus, X, Sparkles, Trash2, CalendarOff } from "lucide-react";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, isWeekend, weekdayName, todayIstIso } from "@/utils/date";
import TaskDialog from "@/components/TaskDialog";
import { STATUS_META, STATUS_ORDER as _SO } from "@/constants/status";
import { ClientTag } from "@/components/ClientTag";

// LIM Flow is India-only — anchor "today" to IST so users filling their log
// between midnight and 5:30 AM don't get pushed back to yesterday by UTC.
const todayISO = () => todayIstIso();

const STATUS_OPTIONS = _SO.map((k) => ({ value: k, label: k === "incomplete" ? "Incomplete (with reason)" : STATUS_META[k].label, activeCls: STATUS_META[k].pill, dot: STATUS_META[k].dot }));

export default function DailyLog() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || !!user?.is_super_admin;
  const outletCtx = useOutletContext() || {};
  const [searchParams] = useSearchParams();
  const blockedMode = searchParams.get("block") === "1";
  const [date, setDate] = useState(searchParams.get("date") || todayISO());
  const [summary, setSummary] = useState("");
  const [overtime, setOvertime] = useState("none");
  const [otClient, setOtClient] = useState("");
  const [onLeave, setOnLeave] = useState(false);
  const [dayType, setDayType] = useState("working"); // working | weekly_off | holiday
  const [tasks, setTasks] = useState([]);
  const [taskUpdates, setTaskUpdates] = useState({}); // { task_id: {status, reason, hold_reason} }
  const [extraTasks, setExtraTasks] = useState([]); // [{id, title, status, reason, hold_reason, client}]
  const [saving, setSaving] = useState(false);
  const [taskTab, setTaskTab] = useState("today"); // prev_week | today | tomorrow | this_week | up_next
  // Manager-only: pick another member to view their filed log
  const [members, setMembers] = useState([]);
  const [viewingUserId, setViewingUserId] = useState(
    isManager && searchParams.get("user_id") && searchParams.get("user_id") !== user?.id
      ? searchParams.get("user_id")
      : null
  );
  const [logExists, setLogExists] = useState(false);
  const [existingLogId, setExistingLogId] = useState(null);
  // "Add self task" dialog — reachable while filling the log so a user can
  // capture ad-hoc work they picked up mid-day without leaving this page.
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [rosterForDialog, setRosterForDialog] = useState([]);
  // Focus-Mode completed to-dos available to import into "Other work done".
  // Loaded only when the caller is filling their own log (not manager-viewing).
  const [focusTodos, setFocusTodos] = useState([]);
  const [todosDismissed, setTodosDismissed] = useState(false);

  // Whose log are we looking at right now
  const targetUserId = viewingUserId || user?.id;
  const isSelf = !viewingUserId || viewingUserId === user?.id;
  // A regular (non-manager) editor may only edit TODAY's or YESTERDAY's own log — anything older is read-only.
  const yesterdayIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const isOlderForEditor = isSelf && !isManager && date < yesterdayIso;
  // Missed older days can be filed once; after that they're read-only.
  const isBackfill = isOlderForEditor && !existingLogId;
  const isPastForEditor = (isOlderForEditor && !!existingLogId) || (isSelf && !isManager && date > todayISO());
  // Managers can edit ANY log (their own or others'). Only editors have read-only past logs.
  const isReadOnly = isPastForEditor || (!isSelf && !isManager);
  const viewingMember = isSelf ? user : members.find((m) => m.id === viewingUserId);

  // Load teammates once (managers only)
  useEffect(() => {
    if (!user || !isManager) return;
    api.get("/users").then(({ data }) => setMembers(data)).catch(() => {});
  }, [user, isManager]);

  // Roster for the self-assign dialog. Editors only need themselves in the
  // list (the dialog auto-locks to a self-chip anyway), but managers need
  // the full team so they can pick a co-assignee if they want.
  useEffect(() => {
    if (!user) return;
    if (isManager) {
      // Reuse the members fetch if already loaded, otherwise pull it.
      if (members.length) setRosterForDialog(members);
      else api.get("/users").then(({ data }) => setRosterForDialog(data)).catch(() => setRosterForDialog([{ id: user.id, name: user.name }]));
    } else {
      setRosterForDialog([{ id: user.id, name: user.name, team: user.team }]);
    }
  }, [user, isManager, members]);

  const load = async () => {
    if (!user) return;
    const uid = targetUserId;
    // 1. Tasks — always fetch everything the caller can see, then filter to the target user
    const { data: t } = await api.get("/tasks");
    const mine = t.filter((x) => x.assigned_to === uid);
    setTasks(mine);
    const init = {};
    mine.forEach((tk) => {
      init[tk.id] = {
        status: tk.status,
        reason: tk.incomplete_reason || "",
        hold_reason: tk.hold_reason || "",
      };
    });
    setTaskUpdates(init);

    // 2. Daily log — ALWAYS pass user_id (even for self). The backend does not
    // auto-restrict managers/super-admins, so omitting user_id would return every
    // teammate's log for the date and we'd pick the wrong doc. This was the source
    // of a data-leak where Amit's extras were showing up under Om/Akanksha.
    const params = { date_from: date, date_to: date, user_id: uid };
    const { data: logs } = await api.get("/daily-logs", { params });
    const existing = logs?.[0];
    setLogExists(!!existing);
    setExistingLogId(existing?.id || null);
    if (existing) {
      setSummary(existing.summary || "");
      setOvertime(existing.overtime || "none");
      setOtClient(existing.overtime_client || "");
      setOnLeave(!!existing.on_leave);
      setDayType(existing.day_type || (isWeekend(date) ? "weekly_off" : "working"));
      const updated = { ...init };
      (existing.task_updates || []).forEach((u) => {
        updated[u.task_id] = {
          status: u.status,
          reason: u.reason || "",
          hold_reason: u.hold_reason || "",
        };
      });
      setTaskUpdates(updated);
      setExtraTasks(existing.extra_tasks || []);
    } else {
      setSummary(""); setOvertime("none"); setOtClient(""); setOnLeave(false);
      setDayType(isWeekend(date) ? "weekly_off" : "working");
      setExtraTasks([]);
    }

    // Focus-Mode to-do pull: fetch the caller's private to-do list when they're
    // filing their own log. We only surface *completed* to-dos that haven't
    // already been imported (extras with matching `id: todo-<id>`).
    if (isSelf) {
      try {
        const { data: todos } = await api.get("/todos");
        setFocusTodos(Array.isArray(todos) ? todos : []);
      } catch { /* silent — the banner just won't appear */ }
    } else {
      setFocusTodos([]);
    }
    setTodosDismissed(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) load(); }, [user, date, viewingUserId]);

  const validate = () => {
    // On-leave logs skip task validation entirely — the person wasn't working.
    if (!onLeave) {
      // Mandatory status: every task scheduled for the log date (bucketFor === "today")
      // must have an explicit status update. This forces editors to close the loop
      // on every assigned task before the log can be saved.
      const dayTasks = tasks.filter((t) => bucketFor(t) === "today");
      const missing = dayTasks.filter((t) => {
        const u = taskUpdates[t.id] || {};
        return !u.status;
      });
      if (missing.length > 0) {
        toast.error(
          `Set a status on every task scheduled for today (${missing.length} missing): ${missing.slice(0, 3).map((t) => `"${t.title}"`).join(", ")}${missing.length > 3 ? "…" : ""}`,
          { duration: 6000 }
        );
        // Auto-switch to the Today tab so the user sees the offenders
        setTaskTab("today");
        return false;
      }
    }
    for (const t of tasks) {
      const u = taskUpdates[t.id] || {};
      if (u.status === t.status) continue;  // untouched legacy rows don't block saving
      if (u.status === "incomplete" && !u.reason?.trim()) {
        toast.error(`"${t.title}" needs an incomplete reason.`); return false;
      }
      if (u.status === "on_hold" && !u.hold_reason?.trim()) {
        toast.error(`"${t.title}" needs an on-hold reason.`); return false;
      }
    }
    for (const [i, e] of extraTasks.entries()) {
      if (!e.title?.trim()) {
        toast.error(`Other work #${i + 1} needs a title.`); return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        log_date: date,
        summary,
        overtime,
        overtime_client: otClient,
        on_leave: onLeave,
        day_type: dayType,
        task_updates: Object.entries(taskUpdates).map(([task_id, v]) => ({
          task_id, status: v.status, reason: v.reason || "", hold_reason: v.hold_reason || "",
        })),
        extra_tasks: extraTasks.map((e) => ({
          id: e.id,
          title: (e.title || "").trim(),
          client: e.client || "",
        })),
      };
      // When a manager is editing another member's log, pass their user_id so the backend
      // saves under that member (not under the manager's own id).
      if (!isSelf) payload.user_id = targetUserId;
      await api.post("/daily-logs", payload);
      toast.success("Daily log saved");
      await load();
      // If the user was being hard-blocked for a missing log, re-run the check.
      // When the missing day gets cleared the AppLayout will stop redirecting
      // them back here on every nav.
      if (outletCtx.refreshLogBlock) {
        const next = await outletCtx.refreshLogBlock();
        if (next && next.blocked === false && blockedMode) {
          toast.success("You're all caught up — jumping back to the whiteboard");
          setTimeout(() => { window.location.href = "/"; }, 600);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ---- Task bucketing (Prev Week / Today / Tomorrow / This Week / Up Next) ----
  // A task is "scheduled" for a range: [start_date OR deadline-day .. deadline-day OR start_date].
  // A task with neither field is Up Next.
  const taskWindow = (t) => {
    const startD = (t.start_date || "").slice(0, 10) || null;
    const endD = (t.deadline || "").slice(0, 10) || null;
    if (!startD && !endD) return null; // Up Next
    const s = startD || endD;
    const e = endD || startD;
    // Ensure ordering (defensive)
    return s <= e ? { s, e } : { s: e, e: s };
  };
  const bucketFor = (t) => {
    const w = taskWindow(t);
    if (!w) return "up_next";
    const d = date; // the currently selected log date
    // Compute reference days as YYYY-MM-DD in the same TZ as the picker
    const base = new Date(d + "T00:00:00");
    const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const today = d;
    const tmrw = iso(new Date(base.getTime() + 86400000));
    // "This week" = Mon..Sun containing the selected date
    const dow = base.getDay(); // 0=Sun..6=Sat — treat Mon as start
    const daysToMon = (dow + 6) % 7;
    const monday = new Date(base.getTime() - daysToMon * 86400000);
    const sunday = new Date(monday.getTime() + 6 * 86400000);
    const weekStart = iso(monday);
    const weekEnd = iso(sunday);
    // Prev week = 7-day window ending yesterday
    const prevStart = iso(new Date(base.getTime() - 7 * 86400000));
    const prevEnd = iso(new Date(base.getTime() - 86400000));

    const overlaps = (a, b) => w.s <= b && w.e >= a;
    if (overlaps(today, today)) return "today";
    if (overlaps(tmrw, tmrw)) return "tomorrow";
    if (overlaps(weekStart, weekEnd)) return "this_week";
    if (overlaps(prevStart, prevEnd) && t.status !== "complete") return "prev_week";
    return null; // out of scope for the selected log day
  };
  const visibleTasks = useMemo(
    () => tasks.filter((t) => bucketFor(t) === taskTab),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, taskTab, date]
  );

  // Focus-Mode → Daily Log: completed to-dos not yet imported into `extra_tasks`.
  // We tag imported extras with `id: todo-<todoId>` so the banner disappears
  // once the user has pulled them in (idempotent across reopens).
  const importableTodos = useMemo(() => {
    if (!isSelf || isReadOnly) return [];
    const existingIds = new Set(extraTasks.map((e) => e.id));
    return focusTodos
      .filter((t) => t.done)
      .filter((t) => !existingIds.has(`todo-${t.id}`));
  }, [focusTodos, extraTasks, isSelf, isReadOnly]);

  const importFocusTodos = () => {
    if (importableTodos.length === 0) return;
    setExtraTasks((prev) => [
      ...prev,
      ...importableTodos.map((t) => ({
        id: `todo-${t.id}`,
        title: (t.text || "").trim(),
        status: "complete",
        reason: "",
        hold_reason: "",
        client: "",
      })),
    ]);
    toast.success(`Pulled ${importableTodos.length} to-do${importableTodos.length === 1 ? "" : "s"} into Other work done`);
  };
  const bucketCounts = useMemo(() => {
    const c = { prev_week: 0, today: 0, tomorrow: 0, this_week: 0, up_next: 0 };
    tasks.forEach((t) => {
      const b = bucketFor(t);
      if (b) c[b] += 1;
    });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, date]);

  const shareWhatsApp = () => {
    const who = viewingMember || user;
    const dtLabel = dayType === "weekly_off" ? "Weekly Off" : dayType === "holiday" ? "Holiday" : null;
    const lines = [
      `*LIM Flow — Daily Report*`,
      `*${who.name}* · ${who.team} · ${fmtDate(date)}`,
      onLeave ? `Status: ON LEAVE` :
        dtLabel && overtime === "none" ? `Status: ${dtLabel} (no work logged)` :
        dtLabel ? `Status: ${dtLabel} — worked as OT` :
        `Status: On Duty`,
      overtime !== "none" ? `Overtime: ${overtime === "half" ? "1/2 Day" : "Full Day"}${otClient ? ` — ${otClient}` : ""}` : null,
      "",
      "*Today's Tasks:*",
      ...tasks.filter((t) => bucketFor(t) === "today").map((t) => {
        const u = taskUpdates[t.id] || {};
        const st = (u.status || t.status).replace("_", " ");
        let extra = "";
        if (u.status === "incomplete" && u.reason) extra = ` (Reason: ${u.reason})`;
        if (u.status === "on_hold" && u.hold_reason) extra = ` (On hold: ${u.hold_reason})`;
        return `• ${t.client ? `*${t.client}* — ` : ""}${t.title} — ${st}${extra}`;
      }),
      extraTasks.length > 0 ? "" : null,
      extraTasks.length > 0 ? "*Other work done:*" : null,
      ...extraTasks.map((e) => `• ${e.client ? `*${e.client}* — ` : ""}${e.title}`),
      "",
      summary ? `*Notes:* ${summary}` : null,
    ].filter(Boolean).join("\n");
    const url = `https://wa.me/?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl">
      <div className="overline flex items-center gap-2"><ClipboardCheck size={12} /> Daily Log</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1 mb-2">
        {isReadOnly && !isSelf ? `${viewingMember?.name}'s daily log` :
         isPastForEditor ? "Past daily log" :
         isBackfill ? "File a missed log" :
         "End-of-day filing"}
      </h1>
      <p className="text-sm text-white/50 mb-6">
        {isReadOnly && !isSelf
          ? `Viewing ${viewingMember?.name}'s filed log for ${fmtDate(date)}. Read-only — pick another date or member to compare.`
          : isPastForEditor
          ? `Read-only view of your log filed on ${fmtDate(date)}. Only managers can edit past entries.`
          : isBackfill
          ? `You missed the log for ${fmtDate(date)}. You can file it once — after saving it can't be changed (ask your manager for corrections).`
          : "File this before you leave. It powers your KRA, the monthly report, and the manager view."}
      </p>

      {/* Manager-only member switcher */}
      {isManager && (
        <div className="glass rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="daily-log-member-switcher">
          <div className="flex items-center gap-2 text-white/50">
            <Eye size={13} className="text-[#E11D2E]" />
            <span className="overline">View log for</span>
          </div>
          <Select
            value={targetUserId}
            onValueChange={(v) => setViewingUserId(v === user.id ? null : v)}
          >
            <SelectTrigger data-testid="daily-log-member-select" className="bg-black/40 border-white/10 h-9 w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10 text-white max-h-80">
              <SelectItem value={user.id}>Me — {user.name}</SelectItem>
              {members
                .filter((m) => m.id !== user.id)
                .sort((a, b) => (a.team || "").localeCompare(b.team || "") || a.name.localeCompare(b.name))
                .map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} · {m.title || m.role} · {m.team}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            data-testid="daily-log-download-ledger"
            onClick={async () => {
              try {
                const month = date.slice(0, 7);
                const res = await api.get("/reports/daily-log-pdf", {
                  params: { month, user_id: targetUserId },
                  responseType: "blob",
                });
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                const uName = (viewingMember?.name || user.name).replace(/\W+/g, "-");
                a.download = `LIM-DailyLog-${month}-${uName}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Monthly ledger downloaded");
              } catch {
                toast.error("Failed to generate ledger");
              }
            }}
            className="h-9 bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30 hover:text-white"
          >
            <Save size={14} className="mr-2" /> Monthly ledger PDF
          </Button>
          {isReadOnly && (
            <>
              <div className="flex items-center gap-2 ml-1 pl-3 border-l border-white/10">
                <Avatar user={viewingMember} size={26} />
                <div className="text-xs">
                  <div className="text-white">{viewingMember?.name}</div>
                  <div className="text-white/40 font-mono text-[0.7rem]">{viewingMember?.title || viewingMember?.role} · {viewingMember?.team}</div>
                </div>
              </div>
              <span
                className={`ml-auto text-[0.7rem] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${
                  logExists
                    ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
                    : "border-amber-500/30 text-amber-300 bg-amber-500/5"
                }`}
              >
                {logExists ? "Log filed" : "Not filed yet"}
              </span>
              <Button
                size="sm" variant="ghost"
                data-testid="daily-log-back-to-me"
                onClick={() => setViewingUserId(null)}
                className="h-8 text-white/60 hover:text-white hover:bg-white/5"
              >
                <ArrowLeft size={12} className="mr-1" /> Back to my log
              </Button>
            </>
          )}
        </div>
      )}

      <div className="glass rounded-xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="overline">Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
            />
            <div className="mt-1 text-xs text-white/40 font-mono">
              {weekdayName(date)}{isWeekend(date) ? " · Weekly Off" : ""}
            </div>
          </div>
          <div>
            <Label className="overline">Day Type</Label>
            <Select value={dayType} onValueChange={setDayType} disabled={isReadOnly || onLeave}>
              <SelectTrigger data-testid="daily-log-day-type" className="bg-black/40 border-white/10 mt-2 disabled:opacity-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white">
                <SelectItem value="working">Working Day</SelectItem>
                <SelectItem value="weekly_off">Weekly Off (Sat/Sun)</SelectItem>
                <SelectItem value="holiday">Public Holiday</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Overtime</Label>
            <Select value={overtime} onValueChange={setOvertime} disabled={isReadOnly}>
              <SelectTrigger data-testid={TID.dailyLogOvertime} className="bg-black/40 border-white/10 mt-2 disabled:opacity-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white">
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="half">1/2 Day OT</SelectItem>
                <SelectItem value="full">Full Day OT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* OT client sits on its own row when relevant */}
        {overtime !== "none" && (
          <div className="mt-4">
            <Label className="overline">OT Client</Label>
            <Input
              data-testid={TID.dailyLogOvertimeClient}
              value={otClient}
              onChange={(e) => setOtClient(e.target.value)}
              disabled={isReadOnly}
              className="bg-black/40 border-white/10 mt-2 disabled:opacity-40 focus-visible:ring-red-500"
              placeholder="Client / project this OT was for"
            />
          </div>
        )}

        {/* Weekly-off / Holiday banner */}
        {!onLeave && dayType !== "working" && (
          <div
            data-testid="daily-log-offday-banner"
            className="mt-4 flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3"
          >
            <CalendarOff size={16} className="text-amber-300 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-100/90 leading-relaxed">
              <span className="font-semibold">{dayType === "weekly_off" ? "Weekly Off" : "Public Holiday"}.</span>{" "}
              {overtime === "none"
                ? "No work is expected — just save the log to record the day off. If you did work today, switch Overtime to 1/2 or Full Day."
                : "Any work you log today will be counted as Overtime. Please add the client above."}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between border border-white/5 rounded-md p-3 bg-black/20">
          <div className="flex items-center gap-3">
            <Coffee size={16} className="text-amber-400" />
            <div>
              <div className="text-sm">On leave today</div>
              <div className="text-xs text-white/40">Mark yourself out for the day.</div>
            </div>
          </div>
          <Switch
            data-testid={TID.dailyLogLeave}
            checked={onLeave}
            onCheckedChange={setOnLeave}
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div className="glass rounded-xl p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="overline">Update tasks · {visibleTasks.length}</div>
          <div className="flex items-center gap-3">
            <div className="text-[0.7rem] text-white/40 font-mono uppercase tracking-widest">
              {taskTab === "today" ? "Only tasks scheduled for today" :
               taskTab === "tomorrow" ? "Preview — tomorrow's plate" :
               taskTab === "this_week" ? "This week (Mon → Sun)" :
               taskTab === "prev_week" ? "Rolled-over from last week" :
               "Unscheduled — pending start / deadline"}
            </div>
            {isSelf && !isReadOnly && (
              <Button
                size="sm"
                type="button"
                onClick={() => setAddTaskOpen(true)}
                data-testid="daily-log-add-self-task"
                className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full h-8 text-xs px-3"
                title="Add a task for yourself — appears here and everywhere else this task belongs"
              >
                <Plus size={13} className="mr-1" /> Add task
              </Button>
            )}
          </div>
        </div>
        {/* Tab strip */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="daily-log-task-tabs">
          {[
            { key: "prev_week", label: "Previous Week" },
            { key: "today", label: "Today" },
            { key: "tomorrow", label: "Tomorrow" },
            { key: "this_week", label: "This Week" },
            { key: "up_next", label: "Up Next" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              data-testid={`daily-log-tab-${tab.key}`}
              onClick={() => setTaskTab(tab.key)}
              className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                taskTab === tab.key
                  ? "bg-[#E11D2E]/20 border-[#E11D2E]/40 text-[#E11D2E]"
                  : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
              }`}
            >
              {tab.label} · {bucketCounts[tab.key] || 0}
            </button>
          ))}
        </div>
        {visibleTasks.length === 0 ? (
          <div className="text-white/40 text-sm border border-dashed border-white/10 rounded-md p-6 text-center">
            {tasks.length === 0
              ? "No tasks assigned to you yet."
              : taskTab === "today"
              ? "Nothing scheduled for today. Try another tab or Up Next."
              : taskTab === "up_next"
              ? "Nothing parked in Up Next — every task has a date."
              : "Nothing in this bucket."}
          </div>
        ) : (
          <div className="space-y-4">
            {visibleTasks.map((t) => {
              const u = taskUpdates[t.id] || { status: t.status, reason: "", hold_reason: "" };
              return (
                <div key={t.id} className="border border-white/8 rounded-md p-4 bg-surface">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-medium text-sm"><ClientTag client={t.client} />{t.title}</div>
                      <div className="text-xs text-white/40 mt-0.5">
                        {t.project_name || ""}
                        {t.start_date ? ` · from ${fmtDate(t.start_date)}` : ""}
                        {t.deadline ? ` · due ${fmtDateTime(t.deadline)}` : (!t.start_date ? " · Up Next" : "")}
                      </div>
                    </div>
                    {t.priority && <span className="text-[0.7rem] px-2 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">HIGH</span>}
                  </div>
                  <RadioGroup
                    value={u.status}
                    onValueChange={(v) => !isReadOnly && setTaskUpdates({ ...taskUpdates, [t.id]: { ...u, status: v } })}
                    disabled={isReadOnly}
                    className="grid grid-cols-2 md:grid-cols-5 gap-2"
                  >
                    {STATUS_OPTIONS.map((s) => {
                      const selected = u.status === s.value;
                      return (
                        <label
                          key={s.value}
                          data-testid={`daily-log-status-${t.id}-${s.value}`}
                          className={`text-xs border rounded-md px-3 py-2 flex items-center gap-2 transition-colors ${
                            isReadOnly ? "cursor-default" : "cursor-pointer"
                          } ${
                            selected ? s.activeCls : "border-white/10 text-white/60 hover:border-white/20"
                          } ${isReadOnly && !selected ? "opacity-40" : ""}`}
                        >
                          <span className={`h-2 w-2 rounded-full ${s.dot} ${selected ? "opacity-100" : "opacity-40"}`} />
                          <RadioGroupItem value={s.value} className="border-white/20 sr-only" disabled={isReadOnly} />
                          {s.label}
                        </label>
                      );
                    })}
                  </RadioGroup>
                  {u.status === "incomplete" && (
                    <Input
                      value={u.reason}
                      readOnly={isReadOnly}
                      onChange={(e) => setTaskUpdates({ ...taskUpdates, [t.id]: { ...u, reason: e.target.value } })}
                      placeholder="Why couldn't this be completed?"
                      className="mt-3 bg-black/40 border-white/10 focus-visible:ring-red-500"
                    />
                  )}
                  {u.status === "on_hold" && (
                    <Input
                      value={u.hold_reason}
                      readOnly={isReadOnly}
                      onChange={(e) => setTaskUpdates({ ...taskUpdates, [t.id]: { ...u, hold_reason: e.target.value } })}
                      placeholder="Why is this on hold?"
                      className="mt-3 bg-black/40 border-white/10 focus-visible:ring-cyan-500"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Other work done — ad-hoc entries beyond assigned tasks.
          Hidden entirely from the fill flow (user's request) so the daily-log
          popup stays focused on task updates + day notes. Still visible in
          read-only mode so managers reviewing someone's filed log can see
          anything that person logged outside of their assigned tasks. */}
      {isReadOnly && (
      <div className="glass rounded-xl p-6 mb-6" data-testid="daily-log-extras">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={13} className="text-[#E11D2E]" />
            <div className="overline">Other work done · {extraTasks.length}</div>
          </div>
          {!isReadOnly && (
            <Button
              size="sm" variant="ghost"
              data-testid="daily-log-add-extra"
              onClick={() =>
                setExtraTasks([
                  ...extraTasks,
                  { id: `local-${Date.now()}`, title: "", status: "complete", reason: "", hold_reason: "", client: "" },
                ])
              }
              className="h-8 text-[#E11D2E] hover:text-white hover:bg-[#E11D2E]/10"
            >
              <Plus size={14} className="mr-1" /> Add work item
            </Button>
          )}
        </div>

        {/* Focus-Mode auto-pull banner — surfaces completed private to-dos so the
            user can move them into the official log with one click. */}
        {!isReadOnly && !todosDismissed && importableTodos.length > 0 && (
          <div
            data-testid="daily-log-todo-pull-banner"
            className="mb-4 rounded-md border border-lime-500/30 bg-lime-500/5 p-3 flex flex-wrap items-center gap-3"
          >
            <Sparkles size={14} className="text-lime-300 shrink-0" />
            <div className="text-xs text-lime-100/90 flex-1 min-w-[220px]">
              You&apos;ve ticked off <span className="font-semibold text-lime-200">{importableTodos.length}</span>{" "}
              Focus To-do{importableTodos.length === 1 ? "" : "s"}. Pull them into today&apos;s log?
              <div className="text-[0.7rem] text-lime-100/60 font-mono mt-1 truncate">
                {importableTodos.slice(0, 3).map((t) => `• ${t.text}`).join("   ")}
                {importableTodos.length > 3 ? `  +${importableTodos.length - 3} more` : ""}
              </div>
            </div>
            <Button
              size="sm"
              data-testid="daily-log-todo-pull-btn"
              onClick={importFocusTodos}
              className="h-8 bg-lime-600/30 border border-lime-500/50 text-lime-100 hover:bg-lime-600/40"
            >
              <Plus size={12} className="mr-1" /> Pull all
            </Button>
            <button
              type="button"
              data-testid="daily-log-todo-pull-dismiss"
              onClick={() => setTodosDismissed(true)}
              className="text-white/40 hover:text-white p-1"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}
        {extraTasks.length === 0 ? (
          <div className="text-xs text-white/30 italic border border-dashed border-white/10 rounded-md p-4 text-center">
            {isReadOnly ? "No other work logged for this day." : "Tap “Add work item” to log anything you did outside of assigned tasks."}
          </div>
        ) : (
          <div className="space-y-3">
            {extraTasks.map((e, idx) => (
              <div key={e.id} className="border border-white/8 rounded-md p-4 bg-surface relative">
                {!isReadOnly && (
                  <button
                    data-testid={`daily-log-extra-remove-${idx}`}
                    onClick={() => setExtraTasks(extraTasks.filter((_, i) => i !== idx))}
                    className="absolute top-2 right-2 text-white/40 hover:text-red-400 p-1 rounded"
                    aria-label="Remove"
                  >
                    <X size={14} />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label className="overline">Work item</Label>
                    <Input
                      data-testid={`daily-log-extra-title-${idx}`}
                      value={e.title}
                      readOnly={isReadOnly}
                      onChange={(ev) =>
                        setExtraTasks(extraTasks.map((x, i) => (i === idx ? { ...x, title: ev.target.value } : x)))
                      }
                      className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                    />
                  </div>
                  <div>
                    <Label className="overline">Client (optional)</Label>
                    <Input
                      value={e.client || ""}
                      readOnly={isReadOnly}
                      onChange={(ev) =>
                        setExtraTasks(extraTasks.map((x, i) => (i === idx ? { ...x, client: ev.target.value } : x)))
                      }
                      className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      <div className="glass rounded-xl p-6 mb-6">
        <Label className="overline">
          Day notes {!onLeave && <span className="text-[#E11D2E]">*</span>}
        </Label>
        <Textarea
          value={summary}
          readOnly={isReadOnly}
          onChange={(e) => setSummary(e.target.value)}
          data-testid="daily-log-summary"
          className="bg-black/40 border-white/10 mt-2 min-h-[100px] focus-visible:ring-red-500"
          placeholder={isReadOnly ? "—" : "Describe what you worked on today. Required — even short bullet points are fine."}
        />
        {!isReadOnly && !onLeave && (
          <p className="text-xs text-white/50 mt-2">
            Required. Everyone must describe their day — even if no task was allotted. Marked <span className="text-[#E11D2E]">*</span> above.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        {!isReadOnly && (
          <Button
            onClick={save}
            disabled={saving || (!onLeave && summary.trim().length < 3)}
            data-testid={TID.dailyLogSaveBtn}
            title={!onLeave && summary.trim().length < 3 ? "Fill in Day notes before saving" : ""}
            className="bg-red-600 hover:bg-red-500 rounded-full px-6 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save Daily Log"}
          </Button>
        )}
        <Button
          onClick={shareWhatsApp}
          data-testid={TID.dailyLogWhatsappBtn}
          variant="outline"
          className="rounded-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"
        >
          <Send size={14} className="mr-2" /> Share on WhatsApp
        </Button>
        {isManager && existingLogId && (
          <Button
            onClick={async () => {
              if (!window.confirm(`Delete ${viewingMember?.name || "this"}'s log for ${fmtDate(date)}? This cannot be undone.`)) return;
              try {
                await api.delete(`/daily-logs/${existingLogId}`);
                toast.success("Log deleted");
                setExistingLogId(null);
                setLogExists(false);
                setSummary(""); setOvertime("none"); setOtClient(""); setOnLeave(false);
                setExtraTasks([]);
              } catch {
                toast.error("Failed to delete log");
              }
            }}
            data-testid="daily-log-delete-btn"
            variant="outline"
            className="rounded-full border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200 ml-auto"
          >
            <Trash2 size={14} className="mr-2" /> Delete this log
          </Button>
        )}
      </div>

      {/* Self-assign task dialog — spawned from the "Add task" button above the
          task list. On save we reload so the new task lands in whichever tab
          matches its start/deadline (usually Today). In-flight radio selections
          on other tasks are preserved across the reload. */}
      {isSelf && (
        <TaskDialog
          open={addTaskOpen}
          onOpenChange={setAddTaskOpen}
          users={rosterForDialog}
          existing={null}
          defaultAssignee={user?.id || null}
          onSaved={async () => {
            setAddTaskOpen(false);
            // Snapshot current in-flight radio choices so a mid-log reload
            // doesn't wipe them.
            const inflight = taskUpdates;
            await load();
            setTaskUpdates((fresh) => {
              const merged = { ...fresh };
              Object.entries(inflight).forEach(([tid, patch]) => {
                if (merged[tid]) merged[tid] = { ...merged[tid], ...patch };
              });
              return merged;
            });
          }}
        />
      )}
    </div>
  );
}
